import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Custom plugin for crosshair
const crosshairPlugin = {
  id: 'crosshair',
  afterDraw: (chart) => {
    if (chart.tooltip?._active?.length) {
      const ctx = chart.ctx;
      const activePoint = chart.tooltip._active[0];
      const x = activePoint.element.x;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;

      // Draw vertical line
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.4)';
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.restore();
    }
  }
};

ChartJS.register(crosshairPlugin);

const LineChart = ({ data, label, color = '#3b82f6', insight, onPointClick }) => {
  const chartData = {
    labels: Object.keys(data),
    datasets: [
      {
        label: label,
        data: Object.values(data),
        borderColor: color,
        backgroundColor: `${color}20`,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBorderWidth: 3,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: '#fff',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onPointClick) {
        const dataIndex = elements[0].index;
        const dateKey = Object.keys(data)[dataIndex];
        const value = Object.values(data)[dataIndex];
        onPointClick(dateKey, value, dataIndex);
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: color,
        borderWidth: 2,
        padding: 16,
        displayColors: false,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].label);
            return date.toLocaleDateString('en-US', { 
              weekday: 'short',
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
          },
          label: (context) => {
            return `${label}: ${context.parsed.y}`;
          },
          afterLabel: (context) => {
            const values = Object.values(data);
            const total = values.reduce((sum, val) => sum + val, 0);
            const percentage = total > 0 ? ((context.parsed.y / total) * 100).toFixed(1) : 0;
            return `${percentage}% of total`;
          },
          footer: () => {
            return '\nClick for details';
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          callback: function(value, index) {
            const date = new Date(this.getLabelForValue(value));
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          },
          maxRotation: 45,
          minRotation: 45,
          color: '#64748b',
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#e2e8f0',
          drawBorder: false,
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 11
          },
          precision: 0
        }
      },
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
  };

  // Calculate insight
  const values = Object.values(data);
  const days = values.length;
  const total = values.reduce((sum, val) => sum + val, 0);
  const average = values.length > 0 ? (total / values.length).toFixed(1) : 0;
  const max = Math.max(...values);
  const maxDate = Object.keys(data)[values.indexOf(max)];
  
  // Calculate trend
  const recentValues = values.slice(-3);
  const olderValues = values.slice(0, -3);
  const recentAvg = recentValues.reduce((sum, val) => sum + val, 0) / recentValues.length;
  const olderAvg = olderValues.length > 0 ? olderValues.reduce((sum, val) => sum + val, 0) / olderValues.length : recentAvg;
  const trend = recentAvg > olderAvg ? 'increasing' : recentAvg < olderAvg ? 'decreasing' : 'stable';
  const trendPercent = olderAvg > 0 ? (((recentAvg - olderAvg) / olderAvg) * 100).toFixed(1) : 0;

  return (
    <div className="chart-container">
      <div className="chart-wrapper">
        <Line data={chartData} options={options} />
      </div>
      <div className="chart-insight">
        <div className="insight-icon">💡</div>
        <div className="insight-content">
          <h4>Key Insight</h4>
          <p>
            {insight || `Average of ${average} per day over the last ${days} days. 
            Peak activity on ${new Date(maxDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} 
            with ${max} ${label.toLowerCase()}. 
            ${trend === 'increasing' ? `📈 Trending up by ${Math.abs(trendPercent)}%` : 
              trend === 'decreasing' ? `📉 Trending down by ${Math.abs(trendPercent)}%` : 
              '➡️ Activity is stable'}.`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LineChart;
